import { Link } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";
import { PACKAGES, ADDONS } from "@/data/content";
import { Check } from "lucide-react";

const POLICIES = [
  { title: "Footage rights", copy: "You receive a full copy of both the raw clips and the edited highlight video. We retain rights to the footage and may use short, anonymized clips in marketing — unless you opt out in the contract. Guests can always decline recording; signage at the booth makes that clear." },
  { title: "Deposit & cancellation", copy: "A 30% non-refundable deposit locks your date. The balance is due 14 days before the event. No refund for weather or venue-caused issues outside our control. One free reschedule is included with 14+ days' notice." },
  { title: "Service area & travel", copy: "Flat pricing within 30km of our Lower Mainland base. Beyond that, travel is $5/km — capped at $200 — and always quoted upfront before you commit." },
];

export default function Packages() {
  return (
    <div className="pt-32">
      <section className="px-5 sm:px-8 pb-14">
        <div className="max-w-[1400px] mx-auto">
          <Reveal><p className="text-xs uppercase tracking-[0.3em] text-forest mb-6">Packages & pricing</p></Reveal>
          <Reveal delay={0.1}><h1 className="font-sans font-extrabold tracking-tight text-5xl sm:text-7xl leading-[1.02] max-w-3xl">No fake tiers. No fine print games.</h1></Reveal>
          <Reveal delay={0.2}><p className="text-ink/60 text-lg mt-6 max-w-2xl">One operator, one beautiful booth. Every package includes delivery, setup, teardown, an edited highlight video and all your raw clips.</p></Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-20">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 gap-6">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <div className={`rounded-2xl p-8 h-full border flex flex-col ${p.featured ? "bg-forest text-cream border-forest" : "bg-cream border-clubborder"}`} data-testid={`package-${i}`}>
                {p.featured && <span className="inline-block self-start text-xs uppercase tracking-widest bg-cream text-forest px-3 py-1 rounded-full mb-4">Most booked</span>}
                <h2 className="font-sans font-bold text-2xl">{p.name}</h2>
                <p className={`text-sm mt-1 ${p.featured ? "text-cream/70" : "text-ink/50"}`}>{p.tagline}</p>
                <p className="font-serif text-6xl mt-6">${p.price}</p>
                <p className={`text-sm mb-6 ${p.featured ? "text-cream/70" : "text-ink/50"}`}>{p.hours} on-site</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.includes.map((inc) => (
                    <li key={inc} className="flex gap-3 text-sm">
                      <Check size={17} className={p.featured ? "text-cream shrink-0 mt-0.5" : "text-forest shrink-0 mt-0.5"} />
                      <span className={p.featured ? "text-cream/90" : "text-ink/70"}>{inc}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/book" className={`block text-center rounded-full py-3.5 text-sm font-medium transition-colors ${p.featured ? "bg-cream text-forest hover:bg-sand" : "bg-forest text-cream hover:bg-forest-deep"}`}>
                  Check availability
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 bg-clubmuted border-y border-clubborder">
        <div className="max-w-[1000px] mx-auto">
          <Reveal><h2 className="font-sans font-extrabold tracking-tight text-4xl mb-10">Add-ons.</h2></Reveal>
          {ADDONS.map((a, i) => (
            <Reveal key={a.label} delay={i * 0.06}>
              <div className="flex justify-between items-baseline gap-6 border-t border-clubborder py-6">
                <div>
                  <h3 className="font-sans font-semibold text-lg">{a.label}</h3>
                  <p className="text-ink/50 text-sm mt-1">{a.note}</p>
                </div>
                <span className="font-serif text-3xl text-forest whitespace-nowrap">{a.price}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-5 sm:px-8 py-24">
        <div className="max-w-[1000px] mx-auto">
          <Reveal><h2 className="font-sans font-extrabold tracking-tight text-4xl mb-12">The policies, in plain English.</h2></Reveal>
          <div className="space-y-10">
            {POLICIES.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="grid md:grid-cols-[220px_1fr] gap-4 md:gap-10 border-t border-clubborder pt-6">
                  <h3 className="font-serif text-2xl text-forest">{p.title}</h3>
                  <p className="text-ink/60 leading-relaxed">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-16 text-center">
              <Link to="/book" className="inline-block rounded-full bg-forest text-cream px-10 py-4 text-base hover:bg-forest-deep transition-colors">Check Your Date</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
